import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import { 
  CREATE_BOOKING_COMMENT_MUTATION,
  UPDATE_BOOKING_COMMENT_MUTATION,
} from '../../pages/bookings/gql';
import { dispatchMessage, dispatchException } from 'helper/snackbar';

interface BookingCommentDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  bookingId: number;
  editingComment?: any;
}

/**
 * Dialog for creating/editing booking-specific comments.
 * Simpler than company comment dialog - no relevance flags or contact subjects.
 */
const BookingCommentDialog: React.FC<BookingCommentDialogProps> = ({
  open,
  onClose,
  onSaved,
  bookingId,
  editingComment,
}) => {
  const dispatch = useDispatch();
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ description?: string }>({});

  const [createCommentMutation] = useMutation(CREATE_BOOKING_COMMENT_MUTATION);
  const [updateCommentMutation] = useMutation(UPDATE_BOOKING_COMMENT_MUTATION);

  const isEditing = !!editingComment;

  useEffect(() => {
    if (editingComment) {
      setDescription(editingComment.description || '');
    } else {
      setDescription('');
    }
    setErrors({});
  }, [editingComment, open]);

  const validate = () => {
    const newErrors: { description?: string } = {};
    
    if (!description || description.trim().length < 10) {
      newErrors.description = String(i18next.t('booking-comment-validation-min'));
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      if (isEditing) {
        await updateCommentMutation({
          variables: {
            id: editingComment.id,
            description: description.trim(),
          },
        });
      } else {
        await createCommentMutation({
          variables: {
            bookingId,
            description: description.trim(),
          },
        });
      }

      dispatchMessage(dispatch, String(i18next.t('booking-comment-save-success')));
      onSaved();
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing 
          ? i18next.t('booking-comment-edit')
          : i18next.t('booking-comment-add')
        }
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={i18next.t('booking-comment-description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description || i18next.t('booking-comment-description-helper')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {i18next.t('cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {i18next.t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingCommentDialog;